// Browser IndexedDB implementation of the package cache — mirrors the
// Tauri/SQLite `design_files` table exactly (see haspdesktop/src-tauri
// migrations/0004_design_packages.sql and docs plan "Design Packages"):
// one row per file, keyed by [packageId, filePath]. `packageId` (manifest.id)
// is the package's stable identity; `packagePath` is a storage detail field
// on each row, not part of the key.

const DB_NAME = 'openhasp_designer'
const STORE_NAME = 'design_files'
const DB_VERSION = 2

let _db = null

function openDB() {
    if (_db) return Promise.resolve(_db)
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION)
        req.onupgradeneeded = (e) => {
            const db = e.target.result
            if (db.objectStoreNames.contains('designs')) db.deleteObjectStore('designs')
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: ['packageId', 'filePath'] })
                store.createIndex('packageId', 'packageId', { unique: false })
            }
        }
        req.onsuccess = (e) => { _db = e.target.result; resolve(_db) }
        req.onerror  = (e) => reject(e.target.error)
    })
}

/**
 * Replaces every file belonging to `packageId` with `rows` inside one
 * transaction (delete-then-insert) — a package is saved as a whole, never
 * file-by-file. Each row is already a complete
 * {packageId, packagePath, filePath, content, encoding, mime, cachedAt, localModified}.
 */
export async function savePackage(packageId, rows) {
    const db = await openDB()
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        const store = tx.objectStore(STORE_NAME)
        const cursorReq = store.index('packageId').openCursor(IDBKeyRange.only(packageId))
        cursorReq.onsuccess = (e) => {
            const cursor = e.target.result
            if (cursor) {
                cursor.delete()
                cursor.continue()
            } else {
                rows.forEach(row => store.put(row))
            }
        }
        tx.oncomplete = () => resolve()
        tx.onerror    = (e) => reject(e.target.error)
    })
}

export async function loadPackage(packageId) {
    const db = await openDB()
    return new Promise((resolve, reject) => {
        const req = db.transaction(STORE_NAME, 'readonly')
            .objectStore(STORE_NAME).index('packageId').getAll(IDBKeyRange.only(packageId))
        req.onsuccess = (e) => resolve(e.target.result)
        req.onerror   = (e) => reject(e.target.error)
    })
}

/** One row per cached package (its manifest.json row) — a lightweight list. */
export async function listPackages() {
    const db = await openDB()
    return new Promise((resolve, reject) => {
        const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll()
        req.onsuccess = (e) => resolve(e.target.result.filter(r => r.filePath === 'manifest.json'))
        req.onerror   = (e) => reject(e.target.error)
    })
}

export async function deletePackage(packageId) {
    const db = await openDB()
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        const cursorReq = tx.objectStore(STORE_NAME).index('packageId').openCursor(IDBKeyRange.only(packageId))
        cursorReq.onsuccess = (e) => {
            const cursor = e.target.result
            if (cursor) {
                cursor.delete()
                cursor.continue()
            }
        }
        tx.oncomplete = () => resolve()
        tx.onerror    = (e) => reject(e.target.error)
    })
}
